import { gsap, ScrollTrigger } from 'gsap/all'
import SplitType from 'split-type'

/** @typedef {'fade' | 'reveal' | 'slideUp' | 'blurFade'} TextAnimType */
/** @typedef {'forward' | 'random' | 'reverse'} TextAnimOrder */
/** @typedef {'char' | 'word'} TextAnimUnit */

/**
 * @typedef {Object} TextAnimOptions
 * @property {boolean} [useScrollTrigger]
 * @property {string} [triggerStart]
 * @property {number} [delay] 発火後のアニメ開始までの遅延（秒）
 * @property {boolean} [once]
 * @property {number} [stagger]
 * @property {number} [charDuration]
 * @property {number} [lineDuration]
 * @property {string} [easeChars]
 * @property {string} [easeLines]
 * @property {TextAnimType} [type] fade | reveal | slideUp | blurFade
 * @property {TextAnimOrder} [order] forward | random | reverse
 * @property {TextAnimUnit} [unit] char | word
 *
 * HTML data attributes (要素ごとに上書き):
 * - data-text-anim-type
 * - data-text-anim-order
 * - data-text-anim-unit
 * - data-text-anim-start  ScrollTrigger の start（例: top 70%）
 * - data-text-anim-delay  発火後の遅延秒数（例: 0.3）
 */

const ANIM_TYPES = ['fade', 'reveal', 'slideUp', 'blurFade']
const ANIM_ORDERS = ['forward', 'random', 'reverse']
const ANIM_UNITS = ['char', 'word']

const REVEAL_CLIP_FROM = 'polygon(0% 0%, 0% 0%, 0% 150%, 0% 150%)'
const REVEAL_CLIP_TO = 'polygon(0% 0%, 100% 0%, 100% 150%, 0% 150%)'

export default class TextAnim {
  /** @param {string | NodeListOf<Element> | Element[]} [selectorOrNodes] */
  /** @param {TextAnimOptions} [options] */
  constructor(selectorOrNodes, options = {}) {
    if (!selectorOrNodes || typeof selectorOrNodes === 'string') {
      const selector = selectorOrNodes || '.js-text-anim'
      this.nodes = Array.from(document.querySelectorAll(selector))
    } else {
      this.nodes = Array.from(selectorOrNodes).filter((node) => node instanceof HTMLElement)
    }

    this.options = {
      useScrollTrigger: options.useScrollTrigger ?? true,
      triggerStart: options.triggerStart ?? 'top 85%',
      delay: options.delay ?? 0,
      once: options.once ?? true,
      stagger: options.stagger ?? 0.03,
      charDuration: options.charDuration ?? 1,
      lineDuration: options.lineDuration ?? 1,
      easeChars: options.easeChars ?? 'power4.out',
      easeLines: options.easeLines ?? 'expo.out',
      type: options.type ?? 'fade',
      order: options.order ?? 'forward',
      unit: options.unit ?? 'char',
    }

    /** @type {Array<{ node: HTMLElement, split: SplitType, tl: gsap.core.Timeline, trigger?: ScrollTrigger, playDelayCall?: gsap.core.Tween, options: ReturnType<TextAnim['resolveNodeOptions']> }>} */
    this.instances = []

    gsap.registerPlugin(ScrollTrigger)
    this.init()
  }

  init() {
    this.destroy()
    this.instances = []

    for (const node of this.nodes) {
      const nodeOptions = this.resolveNodeOptions(node)
      const split = new SplitType(node, { types: this.getSplitTypes(nodeOptions) })
      const tl = this.buildTimeline(node, split, nodeOptions)
      const instance = { node, split, tl, trigger: undefined, playDelayCall: undefined, options: nodeOptions }
      instance.trigger = this.createScrollTrigger(instance)

      this.instances.push(instance)
    }

    ScrollTrigger.refresh()
  }

  /** @param {HTMLElement} node */
  resolveNodeOptions(node) {
    return {
      ...this.options,
      type: this.parseOption(node.dataset.textAnimType, ANIM_TYPES, this.options.type),
      order: this.parseOption(node.dataset.textAnimOrder, ANIM_ORDERS, this.options.order),
      unit: this.parseOption(node.dataset.textAnimUnit, ANIM_UNITS, this.options.unit),
      triggerStart: this.parseTriggerStart(node),
      delay: this.parseDelay(node),
    }
  }

  /** @param {string | undefined} value @param {string[]} allowed @param {string} fallback */
  parseOption(value, allowed, fallback) {
    return allowed.includes(value ?? '') ? value : fallback
  }

  /** @param {HTMLElement} node */
  parseTriggerStart(node) {
    const value = node.dataset.textAnimStart?.trim()
    return value || this.options.triggerStart
  }

  /** @param {HTMLElement} node */
  parseDelay(node) {
    const value = node.dataset.textAnimDelay

    if (value === undefined || value === '') {
      return this.options.delay
    }

    const parsed = parseFloat(value)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : this.options.delay
  }

  /** @param {typeof this.instances[number]} inst */
  clearPlayDelay(inst) {
    inst.playDelayCall?.kill()
    inst.playDelayCall = undefined
  }

  /** @param {typeof this.instances[number]} inst */
  schedulePlay(inst) {
    this.clearPlayDelay(inst)

    if (inst.options.delay > 0) {
      inst.playDelayCall = gsap.delayedCall(inst.options.delay, () => inst.tl.play())
      return
    }

    inst.tl.play()
  }

  /** @param {ReturnType<TextAnim['resolveNodeOptions']>} nodeOptions */
  needsLines(nodeOptions) {
    if (nodeOptions.type === 'reveal') {
      return true
    }

    return nodeOptions.type === 'slideUp' && nodeOptions.unit === 'word'
  }

  /** @param {ReturnType<TextAnim['resolveNodeOptions']>} nodeOptions */
  getSplitTypes(nodeOptions) {
    const types = []

    if (this.needsLines(nodeOptions)) {
      types.push('lines')
    }

    types.push('words')

    if (nodeOptions.unit === 'char') {
      types.push('chars')
    }

    return types.join(', ')
  }

  /** @param {ReturnType<TextAnim['resolveNodeOptions']>} nodeOptions */
  getAnimationTargets(split, nodeOptions) {
    const lines = split.lines ?? []
    const words = split.words ?? []
    const chars = split.chars ?? []

    return {
      lines,
      words,
      chars,
      units: nodeOptions.unit === 'word' ? words : chars,
    }
  }

  /** @param {ReturnType<TextAnim['resolveNodeOptions']>} nodeOptions */
  getStagger(nodeOptions) {
    const fromMap = {
      forward: 'start',
      reverse: 'end',
      random: 'random',
    }

    return {
      each: nodeOptions.stagger,
      from: fromMap[nodeOptions.order],
    }
  }

  /** @param {HTMLElement} node @param {SplitType} split @param {ReturnType<TextAnim['resolveNodeOptions']>} nodeOptions */
  buildTimeline(node, split, nodeOptions) {
    const { lines, words, units } = this.getAnimationTargets(split, nodeOptions)
    const stagger = this.getStagger(nodeOptions)
    const tl = gsap.timeline({ paused: true })

    gsap.set(node, { autoAlpha: 1 })

    switch (nodeOptions.type) {
      case 'reveal':
        this.setRevealInitialState({ lines, words, units }, nodeOptions)
        tl.to(
          units,
          {
            clipPath: REVEAL_CLIP_TO,
            duration: nodeOptions.charDuration,
            ease: nodeOptions.easeChars,
            stagger,
            onStart: () => node.classList.add('is-anim'),
          },
          'typo',
        )
        tl.to(
          lines,
          {
            x: 0,
            duration: nodeOptions.lineDuration,
            ease: nodeOptions.easeLines,
            stagger,
          },
          'typo',
        )
        break

      case 'slideUp':
        this.setSlideUpInitialState({ lines, words, units }, nodeOptions)
        tl.to(units, {
          yPercent: 0,
          duration: nodeOptions.charDuration,
          ease: nodeOptions.easeChars,
          stagger,
          onStart: () => node.classList.add('is-anim'),
        })
        break

      case 'blurFade':
        gsap.set(units, { autoAlpha: 0, filter: 'blur(8px)' })
        tl.to(units, {
          autoAlpha: 1,
          filter: 'blur(0px)',
          duration: nodeOptions.charDuration,
          ease: nodeOptions.easeChars,
          stagger,
          onStart: () => node.classList.add('is-anim'),
        })
        break

      case 'fade':
      default:
        gsap.set(units, { autoAlpha: 0 })
        tl.to(units, {
          autoAlpha: 1,
          duration: nodeOptions.charDuration,
          ease: nodeOptions.easeChars,
          stagger,
          onStart: () => node.classList.add('is-anim'),
        })
        break
    }

    return tl
  }

  /** @param {{ lines: Element[], words: Element[], units: Element[] }} targets @param {ReturnType<TextAnim['resolveNodeOptions']>} nodeOptions */
  setRevealInitialState({ lines, words, units }, nodeOptions) {
    gsap.set(units, { clipPath: REVEAL_CLIP_FROM })
    gsap.set(lines, { x: '40px' })

    if (nodeOptions.unit === 'char') {
      gsap.set(words, { display: 'inline-block', verticalAlign: 'top' })
    }
  }

  /** @param {{ lines: Element[], words: Element[], units: Element[] }} targets @param {ReturnType<TextAnim['resolveNodeOptions']>} nodeOptions */
  setSlideUpInitialState({ lines, words, units }, nodeOptions) {
    if (nodeOptions.unit === 'char') {
      gsap.set(words, {
        overflow: 'hidden',
        display: 'inline-block',
        verticalAlign: 'top',
      })
      gsap.set(units, { display: 'inline-block', yPercent: 100 })
      return
    }

    gsap.set(lines, { overflow: 'hidden' })
    gsap.set(units, { display: 'inline-block', yPercent: 100 })
  }

  /** @param {typeof this.instances[number]} inst */
  createScrollTrigger(inst) {
    const { node, options: nodeOptions } = inst

    if (!nodeOptions.useScrollTrigger) {
      return undefined
    }

    return ScrollTrigger.create({
      trigger: node,
      start: nodeOptions.triggerStart,
      onEnter: () => this.schedulePlay(inst),
      onEnterBack: () => (!nodeOptions.once ? this.schedulePlay(inst) : null),
      once: nodeOptions.once,
    })
  }

  play() {
    for (const { tl } of this.instances) tl.play()
  }

  /** @param {number | HTMLElement} target */
  playOne(target) {
    this.pick(target)?.tl.play()
  }

  restart() {
    for (const { tl } of this.instances) tl.restart(true)
  }

  /** @param {number | HTMLElement} target */
  restartOne(target) {
    this.pick(target)?.tl.restart(true)
  }

  reset() {
    for (const inst of this.instances) this.resetInstance(inst)
  }

  /** @param {number | HTMLElement} target */
  resetOne(target) {
    const inst = this.pick(target)
    if (inst) this.resetInstance(inst)
  }

  enableScroll() {
    this.options.useScrollTrigger = true

    for (const inst of this.instances) {
      if (!inst.trigger) {
        inst.trigger = this.createScrollTrigger(inst)
      } else {
        inst.trigger.enable()
      }
    }

    ScrollTrigger.refresh()
  }

  disableScroll() {
    this.options.useScrollTrigger = false

    for (const inst of this.instances) {
      this.clearPlayDelay(inst)
      inst.trigger?.disable()
      inst.tl.pause(0)
    }
  }

  /** @param {string | NodeListOf<Element> | Element[]} selectorOrNodes */
  setNodes(selectorOrNodes) {
    if (typeof selectorOrNodes === 'string') {
      this.nodes = Array.from(document.querySelectorAll(selectorOrNodes))
    } else {
      this.nodes = Array.from(selectorOrNodes).filter((node) => node instanceof HTMLElement)
    }

    this.init()
  }

  refresh() {
    ScrollTrigger.refresh()
  }

  destroy() {
    for (const inst of this.instances) {
      this.clearPlayDelay(inst)
      inst.trigger?.kill()
      inst.tl.kill()
      inst.split?.revert?.()
    }

    this.instances = []
  }

  /** @param {number | HTMLElement} target */
  pick(target) {
    if (typeof target === 'number') return this.instances[target]
    return this.instances.find((instance) => instance.node === target)
  }

  /** @param {typeof this.instances[number]} inst */
  resetInstance(inst) {
    const { lines, words, units } = this.getAnimationTargets(inst.split, inst.options)

    this.clearPlayDelay(inst)
    inst.tl.pause(0)
    gsap.set(inst.node, { clearProps: 'all' })
    gsap.set([...lines, ...words, ...units], { clearProps: 'all' })

    switch (inst.options.type) {
      case 'reveal':
        this.setRevealInitialState({ lines, words, units }, inst.options)
        break
      case 'slideUp':
        this.setSlideUpInitialState({ lines, words, units }, inst.options)
        break
      case 'blurFade':
        gsap.set(units, { autoAlpha: 0, filter: 'blur(8px)' })
        break
      case 'fade':
      default:
        gsap.set(units, { autoAlpha: 0 })
        break
    }

    gsap.set(inst.node, { autoAlpha: 1 })

    if (inst.options.useScrollTrigger) {
      inst.trigger?.kill()
      inst.trigger = this.createScrollTrigger(inst)
    }
  }
}
